/**
 * Permissions Configuration File
 * This file defines all user permissions based on email and role
 */

// Permission actions
export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',
  
  // Accounts
  VIEW_ACCOUNTS: 'view_accounts',
  ADD_ACCOUNT: 'add_account',
  EDIT_ACCOUNT: 'edit_account',
  DELETE_ACCOUNT: 'delete_account',
  
  // Devices
  VIEW_DEVICES: 'view_devices',
  ADD_DEVICE: 'add_device',
  EDIT_DEVICE: 'edit_device',
  DELETE_DEVICE: 'delete_device',
  VIEW_DEVICE_DETAILS: 'view_device_details',
  
  // Device Types
  VIEW_DEVICE_TYPES: 'view_device_types',
  ADD_DEVICE_TYPE: 'add_device_type',
  EDIT_DEVICE_TYPE: 'edit_device_type',
  DELETE_DEVICE_TYPE: 'delete_device_type',
  
  // Profile
  VIEW_PROFILE: 'view_profile',
  EDIT_PROFILE: 'edit_profile',
  
  // Device Assignments
  VIEW_DEVICE_ASSIGNMENTS: 'view_device_assignments',
  ADD_DEVICE_ASSIGNMENT: 'add_device_assignment',
  EDIT_DEVICE_ASSIGNMENT: 'edit_device_assignment',
  DELETE_DEVICE_ASSIGNMENT: 'delete_device_assignment',
  
  // Quotes
  VIEW_QUOTES: 'view_quotes',
  ADD_QUOTE: 'add_quote',
  EDIT_QUOTE: 'edit_quote',
  DELETE_QUOTE: 'delete_quote',
  APPROVE_QUOTE: 'approve_quote',
  REJECT_QUOTE: 'reject_quote',
};

// Role-based permissions
const ROLE_PERMISSIONS = {
  superadmin: [
    // Full access to everything
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ACCOUNTS,
    PERMISSIONS.ADD_ACCOUNT,
    PERMISSIONS.EDIT_ACCOUNT,
    PERMISSIONS.DELETE_ACCOUNT,
    PERMISSIONS.VIEW_DEVICES,
    PERMISSIONS.ADD_DEVICE,
    PERMISSIONS.EDIT_DEVICE,
    PERMISSIONS.DELETE_DEVICE,
    PERMISSIONS.VIEW_DEVICE_DETAILS,
    PERMISSIONS.VIEW_DEVICE_TYPES,
    PERMISSIONS.ADD_DEVICE_TYPE,
    PERMISSIONS.EDIT_DEVICE_TYPE,
    PERMISSIONS.DELETE_DEVICE_TYPE,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.ADD_QUOTE,
    PERMISSIONS.EDIT_QUOTE,
    PERMISSIONS.DELETE_QUOTE,
    PERMISSIONS.APPROVE_QUOTE,
    PERMISSIONS.REJECT_QUOTE,
    PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS,
  ],
  
  super_admin: [
    // Full access to everything
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ACCOUNTS,
    PERMISSIONS.ADD_ACCOUNT,
    PERMISSIONS.EDIT_ACCOUNT,
    PERMISSIONS.DELETE_ACCOUNT,
    PERMISSIONS.VIEW_DEVICES,
    PERMISSIONS.ADD_DEVICE,
    PERMISSIONS.EDIT_DEVICE,
    PERMISSIONS.DELETE_DEVICE,
    PERMISSIONS.VIEW_DEVICE_DETAILS,
    PERMISSIONS.VIEW_DEVICE_TYPES,
    PERMISSIONS.ADD_DEVICE_TYPE,
    PERMISSIONS.EDIT_DEVICE_TYPE,
    PERMISSIONS.DELETE_DEVICE_TYPE,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.ADD_QUOTE,
    PERMISSIONS.EDIT_QUOTE,
    PERMISSIONS.DELETE_QUOTE,
    PERMISSIONS.APPROVE_QUOTE,
    PERMISSIONS.REJECT_QUOTE,
    PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS,
  ],
  
  admin: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ACCOUNTS,
    PERMISSIONS.ADD_ACCOUNT,
    PERMISSIONS.EDIT_ACCOUNT,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE,
    PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS,
    PERMISSIONS.ADD_DEVICE_ASSIGNMENT,
    PERMISSIONS.EDIT_DEVICE_ASSIGNMENT,
    PERMISSIONS.DELETE_DEVICE_ASSIGNMENT,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.ADD_QUOTE,
    PERMISSIONS.EDIT_QUOTE,
    PERMISSIONS.DELETE_QUOTE,
  ],
  
  viewer: [
    // View-only access - can see devices but cannot add/edit/delete
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_DEVICES,
    PERMISSIONS.VIEW_DEVICE_DETAILS,
    PERMISSIONS.VIEW_DEVICE_TYPES,
    PERMISSIONS.VIEW_PROFILE,
  ],
};

// Email-specific permissions (overrides role permissions)
const EMAIL_PERMISSIONS = {
  
  // No email-specific restrictions - all admin users get device assignment permissions
  // Add specific email restrictions here if needed
  
  // Example: 'restricted@example.com': [
  //   PERMISSIONS.VIEW_DEVICES,
  //   PERMISSIONS.VIEW_DEVICE_DETAILS,
  //   // Limited permissions for specific emails
  // ],
};

/**
 * Get permissions for a user
 * @param {Object} user - User object with email and role
 * @returns {Array} - Array of permission strings
 */
export const getUserPermissions = (user) => {
  if (!user) return [];
  
  const email = user.email?.toLowerCase();
  const role = user.role?.toLowerCase();
  
  // Email-specific permissions take precedence
  if (email && EMAIL_PERMISSIONS[email]) {
    return EMAIL_PERMISSIONS[email];
  }
  
  // Fall back to role-based permissions
  if (role && ROLE_PERMISSIONS[role]) {
    return ROLE_PERMISSIONS[role];
  }
  
  // Default to empty permissions
  return [];
};

/**
 * Check if user has a specific permission
 * @param {Object} user - User object
 * @param {String} permission - Permission to check
 * @returns {Boolean}
 */
export const hasPermission = (user, permission) => {
  const permissions = getUserPermissions(user);
  return permissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object
 * @param {Array} permissionsList - Array of permissions to check
 * @returns {Boolean}
 */
export const hasAnyPermission = (user, permissionsList) => {
  const permissions = getUserPermissions(user);
  return permissionsList.some(permission => permissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - User object
 * @param {Array} permissionsList - Array of permissions to check
 * @returns {Boolean}
 */
export const hasAllPermissions = (user, permissionsList) => {
  const permissions = getUserPermissions(user);
  return permissionsList.every(permission => permissions.includes(permission));
};

const permissionsConfig = {
  PERMISSIONS,
  getUserPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
};

export default permissionsConfig;

